import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Input as JoyInput, FormControl, FormLabel, FormHelperText } from '@mui/joy';

const InputDate = ({ 
  label, 
  format = 'yyyy-mm-dd', 
  value, 
  error, 
  disabled, 
  required, 
  onChange,
  placeholder,
  ...props 
}) => {
  const [inputValue, setInputValue] = useState('');
  const [internalError, setInternalError] = useState('');

  const getPlaceholder = () => {
    switch (format) {
      case 'yyyy':
        return 'YYYY';
      case 'yyyy-mm':
        return 'YYYY-MM';
      default:
        return 'YYYY-MM-DD';
    }
  };

  const getPattern = () => {
    switch (format) {
      case 'yyyy':
        return '^\\d{4}$';
      case 'yyyy-mm':
        return '^\\d{4}[-\\/\\.]\\d{2}$';
      default:
        return '^\\d{4}[-\\/\\.]\\d{2}[-\\/\\.]\\d{2}$';
    }
  };

  const getMask = (inputVal) => {
    const normalized = inputVal.replace(/[\/\.]/g, '-');
    const parts = normalized.split('-');
    
    let digits = '';
    let separators = [];
    
    parts.forEach((part, index) => {
      digits += part.replace(/\D/g, '');
      
      if (index < parts.length - 1) {
        separators.push('-');
      }
    });
    
    switch (format) {
      case 'yyyy':
        return digits.slice(0, 4);
      case 'yyyy-mm':
        if (digits.length <= 4) {
          return digits;
        } else {
          return `${digits.slice(0, 4)}-${digits.slice(4, 6)}`;
        }
      default:
        if (digits.length <= 4) {
          return digits;
        } else if (digits.length <= 6) {
          return `${digits.slice(0, 4)}-${digits.slice(4, 6)}`;
        } else {
          return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
        }
    }
  };

  const validateDate = (dateStr) => {
    const normalized = dateStr.replace(/[\/\.]/g, '-');
    const parts = normalized.split('-');
    const year = parseInt(parts[0], 10);
    
    if (year < 1900 || year > 2100) {
      return 'Invalid year (1900-2100)';
    }
    
    if (format === 'yyyy') {
      return '';
    }
    
    if (parts.length >= 2) {
      const month = parseInt(parts[1], 10);
      
      if (month < 1 || month > 12) {
        return 'Invalid month (1-12)';
      }
      
      if (format === 'yyyy-mm') {
        return '';
      }
      
      if (parts.length >= 3) {
        const day = parseInt(parts[2], 10);
        const daysInMonth = new Date(year, month, 0).getDate();
        
        if (day < 1 || day > daysInMonth) {
          return `Invalid day (1-${daysInMonth} for ${month}/${year})`;
        }
      }
    }
    
    return '';
  };

  useEffect(() => {
    if (value) {
      const parts = value.split('-');
      if (parts.length >= 1) {
        const year = parts[0];
        const month = parts.length >= 2 ? parts[1] : '01';
        const day = parts.length >= 3 ? parts[2] : '01';
        
        switch (format) {
          case 'yyyy':
            setInputValue(`${year}`);
            break;
          case 'yyyy-mm':
            setInputValue(`${year}-${month}`);
            break;
          default:
            setInputValue(`${year}-${month}-${day}`);
        }
      } else {
        setInputValue('');
      }
    } else {
      setInputValue('');
    }
  }, [value, format]);

  const handleInputChange = (e) => {
    const rawValue = e.target.value;
    
    const lastChar = rawValue.charAt(rawValue.length - 1);
    const isLastCharSeparator = /[-\/\.]/.test(lastChar);
    
    let shouldAddSeparator = false;
    
    if (format === 'yyyy-mm' && rawValue.length === 4 && !/[-\/\.]/.test(rawValue.charAt(4))) {
      shouldAddSeparator = true;
    } else if (format === 'yyyy-mm-dd') {
      if ((rawValue.length === 4 && !/[-\/\.]/.test(rawValue.charAt(4))) ||
          (rawValue.length === 7 && !/[-\/\.]/.test(rawValue.charAt(7)))) {
        shouldAddSeparator = true;
      }
    }
    
    let formattedValue;
    if (isLastCharSeparator) {
      const beforeSeparator = rawValue.slice(0, rawValue.length - 1).replace(/[-\/\.]$/, '');
      formattedValue = `${beforeSeparator}-`;
    } else if (shouldAddSeparator) {
      formattedValue = getMask(rawValue);
    } else {
      formattedValue = getMask(rawValue);
    }
    
    setInputValue(formattedValue);
    
    if (formattedValue === '') {
      setInternalError('');
      onChange('');
      return;
    }
    
    const isComplete = (
      (format === 'yyyy' && formattedValue.length === 4) ||
      (format === 'yyyy-mm' && formattedValue.length === 7) ||
      (format === 'yyyy-mm-dd' && formattedValue.length === 10)
    );
    
    if (isComplete) {
      const regex = new RegExp(getPattern());
      
      if (!regex.test(formattedValue)) {
        setInternalError(`Please use ${format} format`);
        return;
      }
      
      const validationError = validateDate(formattedValue);
      setInternalError(validationError);
      
      if (!validationError) {
        const normalized = formattedValue.replace(/[\/\.]/g, '-');
        const parts = normalized.split('-');
        
        let outputValue;
        if (format === 'yyyy') {
          outputValue = `${parts[0]}-01-01`;
        } else if (format === 'yyyy-mm') {
          outputValue = `${parts[0]}-${parts[1]}-01`;
        } else {
          outputValue = normalized;
        }
        
        onChange(outputValue);
      }
    } else {
      setInternalError('');
      
      if (format === 'yyyy' && formattedValue.length === 4) {
        onChange(`${formattedValue}-01-01`);
      } else if (format === 'yyyy-mm' && formattedValue.includes('-') && formattedValue.split('-')[1]?.length === 2) {
        const parts = formattedValue.split('-');
        onChange(`${parts[0]}-${parts[1]}-01`);
      }
    }
  };

  return (
    <FormControl error={!!(error || internalError)} required={required} disabled={disabled}>
      <FormLabel>{label}</FormLabel>
      <JoyInput
        {...props}
        type="text"
        placeholder={placeholder || getPlaceholder()}
        value={inputValue}
        onChange={handleInputChange}
        error={!!(error || internalError)}
        disabled={disabled}
      />
      <FormHelperText>{error || internalError}</FormHelperText>
    </FormControl>
  );
};

InputDate.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  format: PropTypes.oneOf(['yyyy-mm-dd', 'yyyy-mm', 'yyyy']),
  error: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
};

InputDate.defaultProps = {
  format: 'yyyy-mm-dd',
  value: '',
  error: null,
  disabled: false,
  required: false,
  onChange: () => {},
  placeholder: '',
};

export default InputDate;